<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header></vue-header>
    <v-main  :style="$vuetify.breakpoint.xs ? 'padding:0px!important;margin-top: 100px;' : 'padding:0px!important;margin-top: 140px;'">
      <v-container fluid style="margin-bottom:150px;" :class=" this.$vuetify.breakpoint.xs  ? 'px-0 py-0' : 'px-6 py-6'">
        <v-row class="mt-3" style="margin: 0px;">
          <v-col cols="12" class="text-center">
            <h1 class="text-h3 font-weight-bold mb-4">{{ showJackpotWheel ? jackpotWheelSettings.title : wheelSettings.title }}</h1>
            <p class="subtitle-1 mb-6">
              {{ showJackpotWheel ? 'Spin the jackpot wheel for a chance to win exclusive prizes!' : 'Spin the wheel for a chance to win amazing prizes!' }}
            </p>
          </v-col>
        </v-row>

        <v-row style="margin: 0px;">
          <v-col cols="12" md="8" offset-md="2" class="text-center" :class="{ 'jackpot-card-container': showJackpotWheel }">
            <v-card class="card-shadow border-radius-xl" :class="{ 'jackpot-card': showJackpotWheel }">
              <v-card-text class="text-center py-6">
                <div v-if="loading" class="text-center py-8">
                  <clip-loader :loading="loading" color="#1D75F4"></clip-loader>
                  <p class="mt-4">Loading the wheel...</p>
                </div>

                <div v-else-if="!wheelSettings.active" class="text-center py-8">
                  <v-icon size="64" color="grey">mdi-timer-sand-empty</v-icon>
                  <h2 class="text-h5 mt-4">The spinning wheel is currently unavailable.</h2>
                  <p class="mt-2">Please check back later!</p>
                </div>

                <div v-else-if="wheelSettings.requireLogin && !isLoggedIn" class="text-center py-8">
                  <v-icon size="64" color="primary">mdi-account-lock</v-icon>
                  <h2 class="text-h5 mt-4">Please log in to spin the wheel</h2>
                  <v-btn color="primary" class="mt-4" :to="'/login'">
                    Log In
                  </v-btn>
                </div>

                <div v-else-if="remainingSpins === 0 && !hasReferralSpin" class="text-center py-8">
                  <v-icon size="64" color="amber">mdi-timer-sand</v-icon>
                  <h2 class="text-h5 mt-4">You've used all your spins for now</h2>
                  <p class="mt-2" style="color: #2874f0;">
                    {{ getSpinLimitMessage() }}
                  </p>
                  <div v-if="isLoggedIn" class="referral-link-container mt-4">
                    <p class="subtitle-1 font-weight-bold">Your Referral Link:</p>
                    <v-text-field
                      v-model="referralLink"
                      readonly
                      outlined
                      dense
                      hide-details
                      append-icon="fas fa-copy"
                      @click:append="copyReferralLink"
                      class="referral-link-field"
                    ></v-text-field>
                    <v-alert
                      v-if="linkCopied"
                      type="green"
                      dense
                      class="mt-2"
                      transition="scale-transition"
                    >
                      Link copied to clipboard!
                    </v-alert>
                  </div>
                </div>



                <div v-else>
                  <!-- Wheel Mode Selector -->
                  <div v-if="jackpotSpins > 0" class="text-center mb-4">
                    <v-chip-group
                      v-model="showJackpotWheel"
                      mandatory
                      active-class="primary--text"
                    >
                      <v-chip
                        :value="false"
                        outlined
                        :color="!showJackpotWheel ? 'primary' : ''"
                      >
                        <v-icon left>mdi-rotate-right</v-icon>
                        Spin to Win
                      </v-chip>
                      <v-chip
                        :value="true"
                        outlined
                        :color="showJackpotWheel ? 'amber darken-2' : ''"
                        class="jackpot-chip"
                      >
                        <v-icon left>mdi-star-circle</v-icon>
                        Jackpot ({{ jackpotSpins }})
                      </v-chip>
                    </v-chip-group>
                  </div>

                  <!-- Jackpot Wheel Info -->
                  <div v-if="showJackpotWheel" class="text-center py-2 mb-4">
                    <v-alert color="amber darken-2" outlined>
                      <v-icon left>mdi-star-circle</v-icon>
                      <strong>Jackpot Wheel</strong> - You have {{ jackpotSpins }} jackpot spin{{ jackpotSpins !== 1 ? 's' : '' }} remaining
                    </v-alert>
                  </div>

                  <!-- Regular Wheel Info -->
                  <div v-else>
                    <div v-if="hasReferralSpin" class="text-center py-4 mb-4">
                      <v-alert type="green" outlined>
                        <strong>You have +{{ extraSpins.length }} extra spin from a referral!</strong>
                      </v-alert>
                    </div>

                    <!-- Referral Progress -->
                    <div class="text-center py-2 mb-4" v-if="jackpotSpins === 0">
                      <p class="subtitle-1 mb-2">Refer friends to earn jackpot spins!</p>
                      <v-progress-linear
                        :value="(referralsSinceLastJackpot / referralsNeeded) * 100"
                        height="20"
                        rounded
                        color="amber darken-2"
                        class="jackpot-progress"
                      >
                        <template v-slot:default>
                          <strong>{{ referralsSinceLastJackpot }} / {{ referralsNeeded }} Referrals to Jackpot</strong>
                        </template>
                      </v-progress-linear>
                    </div>
                  </div>
                  <spinning-wheel
                    :segments="showJackpotWheel ? jackpotWheelSegments : wheelSegments"
                    :colors="showJackpotWheel ? jackpotWheelColors : wheelColors"
                    :is-jackpot="showJackpotWheel"
                    @prize-won="onPrizeWon"
                  ></spinning-wheel>

                  <!-- Show remaining spins for regular wheel only -->
                  <div v-if="!showJackpotWheel && wheelSettings.spinLimit > 0" class="mt-4">
                    <div v-if="remainingSpins === null || refreshingSpins" class="d-flex align-center justify-center">
                      <clip-loader :loading="true" color="#1D75F4" :size="16" class="mr-2"></clip-loader>
                      <p class="subtitle-2 mb-0">Loading remaining spins...</p>
                    </div>

                    <div v-else class="d-flex align-center justify-center">
                      <p class="subtitle-2 mb-0 mr-2">
                        You have {{ remainingSpins }} spin{{ remainingSpins !== 1 ? 's' : '' }} remaining
                        {{ wheelSettings.spinLimitPeriod !== 'forever' ? 'today' : '' }}
                      </p>
                      <v-btn
                        icon
                        x-small
                        @click="fetchRemainingSpins"
                        title="Refresh remaining spins"
                        :loading="refreshingSpins"
                      >
                        <v-icon small>mdi-refresh</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-6" style="margin: 0px;">
          <v-col cols="12" md="8" offset-md="2">
            <v-card class="card-shadow border-radius-xl">
              <v-card-title class="d-flex align-center">
                Recent Winners
                <v-spacer></v-spacer>
                <v-btn icon small @click="loadRecentWinners" :loading="loadingWinners" title="Refresh winners">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text>
                <div v-if="loadingWinners" class="text-center py-4">
                  <clip-loader :loading="loadingWinners" color="#1D75F4" :size="20"></clip-loader>
                  <p class="mt-2">Loading recent winners...</p>
                </div>

                <div v-else-if="recentWinners.length === 0" class="text-center py-4">
                  <v-icon size="48" color="grey">mdi-trophy-outline</v-icon>
                  <p class="mt-2">No winners yet. Be the first to win!</p>
                </div>

                <v-list v-else>
                  <v-list-item v-for="(winner, index) in recentWinners" :key="index">
                    <v-list-item-avatar>
                      <v-avatar color="blue" size="40">
                        <span class="white--text">{{ winner.name.charAt(0) }}</span>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ winner.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        Won {{ winner.prize }} on {{ formatDate(winner.date) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Prize Won Dialog -->
      <v-dialog v-model="prizeDialog" max-width="500">
        <v-card>
          <v-card-title class="headline text-center">
            <v-icon large color="amber darken-2" class="mr-2">mdi-trophy</v-icon>
            Congratulations!
          </v-card-title>
          <v-card-text class="text-center">
            <div class="prize-content py-4">
              <h2 class="text-h4 mb-4">You won:</h2>
              <div class="prize-display mb-4">
                {{ currentPrize ? currentPrize.label : '' }}
              </div>

              <p v-if="currentPrize && currentPrize.description" class="mb-4">
                {{ currentPrize.description }}
              </p>

              <template v-if="currentPrize && currentPrize.type === 'discount'">
                <v-alert type="success" outlined>
                  Use code <strong>{{ currentPrize.code }}</strong> at checkout
                </v-alert>
              </template>

              <template v-if="currentPrize && currentPrize.type === 'gift_card'">
                <v-alert type="success" outlined>
                  Your gift card code: <strong>{{ giftCardCode }}</strong>
                </v-alert>
              </template>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn dark color="green" @click="closePrizeDialog">
              Awesome!
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
    <vue-footer></vue-footer>
  </v-app>
</template>

<script>
import SpinningWheel from '@/components/Games/SpinningWheel.vue';
import ClipLoader from 'vue-spinner/src/PulseLoader.vue';
import moment from 'moment';

export default {
  name: 'SpinningWheelGame',
  components: {
    SpinningWheel,
    'RotateLoader': ClipLoader
  },
  data() {
    return {
      loading: true,
      wheelSegments: [],
      jackpotWheelSegments: [],
      jackpotWheelColors: [],
      wheelColors: [],
      wheelSettings: {
        title: 'Spin to Win',
        spinDuration: 5,
        spinLimit: 1,
        spinLimitPeriod: 'day',
        requireLogin: true,
        active: true
      },
      jackpotWheelSettings: {
        title: 'Jackpot Spin',
        spinDuration: 5,
        spinLimit: 0, // No limit for jackpot spins
        spinLimitPeriod: 'forever',
        requireLogin: true,
        active: true
      },

      // User state
      isLoggedIn: false,
      user: null,
      remainingSpins: null,
      referralLink: '',
      linkCopied: false,

      // Jackpot spin state
      jackpotSpins: 0,
      referralsNeeded: 5,
      referralsSinceLastJackpot: 0,
      showJackpotWheel: false,

      // Prize dialog
      prizeDialog: false,
      currentPrize: null,
      giftCardCode: '',

      // Recent winners (for display)
      recentWinners: [],
      loadingWinners: false,
      refreshingSpins: false
    };
  },
  created() {
    this.checkLoginStatus();
    this.loadWheelData();
    this.loadRecentWinners();
    this.fetchJackpotSpins();

  },
  methods: {
    checkLoginStatus() {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      this.isLoggedIn = !!token;

      if (this.isLoggedIn) {
        this.loadUserData();
        // Fetch remaining spins immediately
        this.fetchRemainingSpins();
      } else {
        // Set default remaining spins for non-logged in users
        this.remainingSpins = 0;
      }
    },

    loadUserData() {
      // Fetch user data from the server
      axios.get('/api/user', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(response => {
          this.user = response.data;
          // Generate referral link
          this.generateReferralLink();
          // After loading user data, fetch the remaining spins
          this.fetchRemainingSpins();
        })
        .catch(error => {
          console.error('Error loading user data:', error);
        });
    },

    generateReferralLink() {
    
      if (this.user && this.user.user.user_id) {
        // Create referral link based on current domain and user ID
        const baseUrl = window.location.origin;
        this.referralLink = `${baseUrl}/form-ref/${this.user.user.user_id}`;
      }
    },

    copyReferralLink() {
      // Copy referral link to clipboard
      navigator.clipboard.writeText(this.referralLink)
        .then(() => {
          this.linkCopied = true;
          // Hide the success message after 3 seconds
          setTimeout(() => {
            this.linkCopied = false;
          }, 3000);
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    },

    fetchRemainingSpins() {
      // Set loading state
      this.refreshingSpins = true;

      // Fetch remaining spins directly from the API
      if (this.isLoggedIn) {
        axios.get('/api/spinning-wheel/user-spins', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
          .then(response => {
            if (response.data.flag === 1) {
              // Set remaining spins from API response
              if (response.data.remaining_spins !== null && response.data.remaining_spins !== undefined) {
                this.remainingSpins = response.data.remaining_spins;
              } else {
                // Fallback to calculating remaining spins locally
                this.checkRemainingSpins();
              }

              // Check for referral spins
              this.hasReferralSpin = response.data.has_referral_spin || false;
              this.extraSpins = response.data.extra_spins || [];
            } else {
              // Fallback to calculating remaining spins locally
              this.checkRemainingSpins();
            }
          })
          .catch(error => {
            console.error('Error fetching remaining spins:', error);
            // Fallback to calculating remaining spins locally
            this.checkRemainingSpins();
          })
          .finally(() => {
            this.refreshingSpins = false;
          });
      } else {
        // Not logged in, use default spin limit
        this.remainingSpins = this.wheelSettings.spinLimit || 0;
        this.refreshingSpins = false;
      }
    },

    checkRemainingSpins() {
      if (!this.user || !this.wheelSettings) return;

      // If no limit, user has unlimited spins
      if (this.wheelSettings.spinLimit === 0) {
        this.remainingSpins = Infinity;
        return;
      }

      // Check if user has spins data
      if (!this.user.spins) {
        this.remainingSpins = this.wheelSettings.spinLimit;
        return;
      }

      // Check if spin limit period has passed
      const lastSpin = new Date(this.user.spins.last_spin);
      const now = new Date();
      let resetSpins = false;

      switch (this.wheelSettings.spinLimitPeriod) {
        case 'day':
          resetSpins = !this.isSameDay(lastSpin, now);
          break;
        case 'week':
          resetSpins = !this.isSameWeek(lastSpin, now);
          break;
        case 'month':
          resetSpins = !this.isSameMonth(lastSpin, now);
          break;
        case 'forever':
          resetSpins = false;
          break;
      }

      if (resetSpins) {
        this.remainingSpins = this.wheelSettings.spinLimit;
      } else {
        this.remainingSpins = Math.max(0, this.wheelSettings.spinLimit - this.user.spins.count);
      }
    },

    loadWheelData() {
      this.loading = true;

      // Fetch the wheel configuration from the API
      axios.get('/api/spinning-wheel/config?category=inventory')
        .then(response => {
          if (response.data.flag === 1) {
            // If data exists, use it
            if (response.data.segments && response.data.segments.length > 0) {
              this.wheelSegments = response.data.segments;
            } else {
              // Use default segments if none exist in the database
              this.wheelSegments = [
                {
                  label: '10% OFF',
                  type: 'discount',
                  value: 10,
                  code: 'SPIN10',
                  probability: 0.25,
                  color: '#FF6384',
                  description: '10% discount on your next purchase'
                },
                {
                  label: '25% OFF',
                  type: 'discount',
                  value: 25,
                  code: 'SPIN25',
                  probability: 0.15,
                  color: '#36A2EB',
                  description: '25% discount on your next purchase'
                },
                {
                  label: '50 Points',
                  type: 'points',
                  value: 50,
                  probability: 0.2,
                  color: '#FFCE56',
                  description: '50 reward points added to your account'
                },
                {
                  label: '$10 Gift Card',
                  type: 'gift_card',
                  value: 10,
                  code: 'GIFT',
                  probability: 0.1,
                  color: '#4BC0C0',
                  description: '$10 gift card for your next purchase'
                },
                {
                  label: 'Free Item',
                  type: 'custom',
                  description: 'Get a free item with your next purchase!',
                  probability: 0.15,
                  color: '#9966FF'
                },
                {
                  label: 'Try Again',
                  type: 'custom',
                  description: 'Better luck next time!',
                  probability: 0.15,
                  color: '#FF9F40'
                }
              ];
            }

            // Load settings if they exist
            if (response.data.settings) {
              this.wheelSettings = response.data.settings;
            }

            // Set wheel colors
            this.wheelColors = this.wheelSegments.map(segment => segment.color);

            // Only check remaining spins if we haven't fetched them from the API yet
            if (this.remainingSpins === null) {
              this.checkRemainingSpins();
            }
          } else {
            // Show error message
            console.error('Error loading wheel data:', response.data.message);
          }
        })
        .catch(error => {
          console.error('Error loading wheel data:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadRecentWinners() {
      // Set loading state
      this.loadingWinners = true;

      // Fetch recent winners from the API
      axios.get('/api/spinning-wheel/winners')
        .then(response => {
          if (response.data.flag === 1 && response.data.winners) {
            // Transform the data to match our format
            this.recentWinners = response.data.winners.map(winner => ({
              name: winner.name || 'Anonymous',
              prize: winner.prize,
              date: new Date(winner.date)
            }));
          }
        })
        .catch(error => {
          console.error('Error loading recent winners:', error);
        })
        .finally(() => {
          this.loadingWinners = false;
        });
    },
    onPrizeWon(prize) {
      console.log('Prize won:', prize);

      this.currentPrize = prize;

      // Generate a gift card code if needed
      if (prize.type === 'gift_card') {
        this.giftCardCode = this.generateGiftCardCode(prize.code);
      }

      // If in jackpot wheel mode, use jackpot spin
      if (this.showJackpotWheel) {
        // Record the jackpot prize in the database if user is logged in
        if (this.isLoggedIn) {
          axios.post('/api/spinning-wheel/use-jackpot-spin', {
            prize: prize,
            gift_card_code: this.giftCardCode
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          })
            .then(response => {
              if (response.data.flag === 1) {
                // Update available jackpot spins
                this.jackpotSpins = response.data.available_jackpot_spins;

                // Refresh recent winners list
                this.loadRecentWinners();

                // Show success message
                window.Vue.$toast.success('Jackpot prize recorded successfully!', {
                  position: 'top-right'
                });
              } else {
                window.Vue.$toast.error(response.data.message || 'Failed to record prize', {
                  position: 'top-right'
                });
              }
            })
            .catch(error => {
              console.error('Error recording jackpot prize:', error);
              window.Vue.$toast.error('Failed to record prize', {
                position: 'top-right'
              });
            });
        }
      } else {
        // Temporarily decrement remaining spins for immediate feedback
        // The actual value will be updated from the API response
        if (this.remainingSpins !== Infinity && this.remainingSpins > 0) {
          this.remainingSpins--;
        }

        // Record the prize in the database if user is logged in
        if (this.isLoggedIn) {
          axios.post('/api/spinning-wheel/prize', {
            prize: prize,
            gift_card_code: this.giftCardCode
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          })
            .then(response => {
              if (response.data.flag === 1) {
                // Update user data
                if (this.user && this.user.spins) {
                  this.user.spins.count++;
                  this.user.spins.last_spin = new Date();
                }

                // Update remaining spins if provided in the response
                if (response.data.remaining_spins !== null && response.data.remaining_spins !== undefined) {
                  this.remainingSpins = response.data.remaining_spins;
                }

                // Refresh recent winners list
                this.loadRecentWinners();
              } else {
                console.error('Error recording prize:', response.data.message);
              }
            })
            .catch(error => {
              console.error('Error recording prize:', error);
            });
        }
      }

      // Show the prize dialog
      this.prizeDialog = true;
    },

    closePrizeDialog() {
      this.prizeDialog = false;
      this.currentPrize = null;
      this.giftCardCode = '';

      // Refresh spins after closing the dialog
      if (this.isLoggedIn) {
        this.fetchRemainingSpins();
        this.fetchJackpotSpins();

        // If we were in jackpot mode and have no more jackpot spins, switch back to regular mode
        if (this.showJackpotWheel && this.jackpotSpins <= 0) {
          this.showJackpotWheel = false;
          this.loadWheelData();
        }
      }
    },

    generateGiftCardCode(prefix) {
      // Generate a random code with the given prefix
      const randomPart = Math.random().toString(36).substring(2, 10).toUpperCase();
      return `${prefix}-${randomPart}`;
    },

    getSpinLimitMessage() {
      switch (this.wheelSettings.spinLimitPeriod) {
        case 'day':
          return 'Come back tomorrow for more spins! Or, refer more users with the referral link to get more spins!';
        case 'week':
          return 'Come back next week for more spins! Or, refer more users with the referral link to get more spins!';
        case 'month':
          return 'Come back next month for more spins! or refer more users with your referral link to get more spins.';
        case 'forever':
          return 'You\'ve used all your available spins. Refer more users with your referral link to get more spins.';
        default:
          return 'Come back later for more spins! or refer more users with your referral link to get more spins.';
      }
    },

    formatDate(date) {
      return moment(date).format('MMM D, YYYY');
    },

    isSameDay(date1, date2) {
      return moment(date1).isSame(moment(date2), 'day');
    },

    isSameWeek(date1, date2) {
      return moment(date1).isSame(moment(date2), 'week');
    },

    isSameMonth(date1, date2) {
      return moment(date1).isSame(moment(date2), 'month');
    },

    fetchJackpotSpins() {
      if (!this.isLoggedIn) return;

      axios.get('/api/spinning-wheel/jackpot-spins', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(response => {
        if (response.data.flag === 1) {
          this.jackpotSpins = response.data.available_jackpot_spins || 0;
          this.referralsNeeded = response.data.referrals_needed || 5;
          this.referralsSinceLastJackpot = response.data.referrals_since_last_jackpot || 0;
          this.loadJackpotWheelData();
        }
      })
      .catch(error => {
        console.error('Error loading jackpot spins:', error);
      });
    },

    toggleJackpotWheel() {
      if (this.jackpotSpins <= 0) {
        window.Vue.$toast.error('You have no jackpot spins available', {
          position: 'top-right'
        });
        return;
      }

      this.showJackpotWheel = !this.showJackpotWheel;

      if (this.showJackpotWheel) {
        // Load jackpot wheel data
        this.loadJackpotWheelData();
      } else {
        // Load regular wheel data
        this.loadWheelData();
      }
    },

    loadJackpotWheelData() {
      this.loading = true;

      // Fetch the jackpot wheel configuration from the API
      axios.get('/api/spinning-wheel/config?category=jackpot')
        .then(response => {
          if (response.data.flag === 1) {
            // If data exists, use it
            if (response.data.segments && response.data.segments.length > 0) {
              this.jackpotWheelSegments = response.data.segments;
            } else {
              // Use default jackpot segments if none exist in the database
              this.jackpotWheelSegments = [
                {
                  label: '50% OFF',
                  type: 'discount',
                  value: 50,
                  code: 'JACKPOT50',
                  probability: 0.2,
                  color: '#FF6384',
                  description: '50% discount on your next purchase',
                  discountType: 'percentage'
                },
                {
                  label: '$50 Gift Card',
                  type: 'gift_card',
                  value: 50,
                  code: 'GIFTJACKPOT',
                  probability: 0.2,
                  color: '#36A2EB',
                  description: '$50 gift card for your next purchase'
                },
                {
                  label: '500 Points',
                  type: 'points',
                  value: 500,
                  probability: 0.2,
                  color: '#FFCE56',
                  description: '500 points added to your account'
                },
                {
                  label: 'Free Premium Service',
                  type: 'custom',
                  description: 'Get a free premium service with your next purchase!',
                  probability: 0.2,
                  color: '#4BC0C0'
                },
                {
                  label: 'VIP Status Upgrade',
                  type: 'custom',
                  description: 'Get upgraded to VIP status for 3 months!',
                  probability: 0.2,
                  color: '#9966FF'
                }
              ];
            }

            // Extract colors from segments
            this.jackpotWheelColors = this.jackpotWheelSegments.map(segment => segment.color);

            // Get settings
            if (response.data.settings) {
              // Store jackpot settings separately to avoid overwriting regular wheel settings
              this.jackpotWheelSettings = response.data.settings;
            }
          } else {
            console.error('Error loading jackpot wheel data:', response.data.message);
          }
        })
        .catch(error => {
          console.error('Error loading jackpot wheel data:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.prize-content {
  text-align: center;
  padding: 20px 0;
}

.prize-display {
  font-size: 28px;
  font-weight: bold;
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  color: #4CAF50;
}

.jackpot-chip {
  animation: pulse 1.5s infinite alternate;
  transition: all 0.3s ease;
}

.jackpot-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 0 10px rgba(255, 193, 7, 0.7);
}

.jackpot-progress {
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.jackpot-card-container {
  position: relative;
}

.jackpot-card-container::before {
  content: '';
  position: absolute;
  top: -20px;
  left: -20px;
  right: -20px;
  bottom: -20px;
  background: radial-gradient(circle, rgba(255,215,0,0.15) 0%, rgba(255,255,255,0) 70%);
  z-index: -1;
  border-radius: 50%;
  pointer-events: none;
}

.jackpot-card {
  background: linear-gradient(to bottom, #fffde7 0%, #ffffff 100%) !important;
  border: 1px solid rgba(255, 215, 0, 0.3) !important;
  box-shadow: 0 4px 20px rgba(255, 193, 7, 0.2) !important;
}

.referral-link-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.referral-link-field {
  background-color: white;
  border-radius: 4px;
}

.referral-link-field >>> .v-input__append-inner {
  cursor: pointer;
  color: #4CAF50;
}

.referral-link-field >>> .v-input__append-inner:hover {
  color: #2E7D32;
}

@keyframes pulse {
  from {
    box-shadow: 0 0 5px rgba(255, 193, 7, 0.5);
  }
  to {
    box-shadow: 0 0 10px rgba(255, 193, 7, 0.8);
  }
}
</style>
